/**
 * Displays available assets for a specific artifact version (primarily for PyPI packages).
 * Allows the user to select which distribution file to download.
 */

import { useVersionAssets } from '@/hooks/use-artifact-download';

interface AssetSelectorProps {
  projectKey: string;
  artifactId: string;
  version: string;
  selectedAsset: string | null;
  onAssetSelect: (asset: string | null) => void;
}

/**
 * Formats a byte count into a human-readable file size string.
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

export function AssetSelector({
  projectKey,
  artifactId,
  version,
  selectedAsset,
  onAssetSelect,
}: AssetSelectorProps) {
  const { data: assets, isLoading, isError, error } = useVersionAssets(projectKey, artifactId, version);

  if (isLoading) {
    return (
      <div className="space-y-2" aria-label="Loading assets">
        {[1, 2].map((i) => (
          <div key={i} className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
        <p className="text-sm text-red-700 dark:text-red-300">
          Failed to load assets: {error?.message ?? 'Unknown error'}
        </p>
      </div>
    );
  }

  if (!assets || assets.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-6 text-center">
        <p className="text-sm text-content-tertiary">No assets available for this version.</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs font-medium text-content-secondary mb-1">Select Asset</p>
      <div className="space-y-1 rounded-lg border border-border p-2">
        {assets.map((asset) => {
          const isSelected = selectedAsset === asset.name;
          return (
            <button
              key={asset.name}
              type="button"
              onClick={() => onAssetSelect(isSelected ? null : asset.name)}
              className={[
                'w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600',
                isSelected
                  ? 'border border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
                  : 'border border-transparent hover:bg-surface-raised text-content',
              ].join(' ')}
            >
              <span className="font-medium truncate">{asset.name}</span>
              <span className="ml-2 text-xs text-content-tertiary whitespace-nowrap">
                {formatFileSize(asset.size)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
